// src/fleet-stats/analytics/robotHealth.js
//
// Robot Readiness Index (RRI)
// ─────────────────────────────────────────────────────────────────────────────
// Based on Bechtsis et al. (2022), "Data-driven autonomous robot performance
// assessment", International Journal of Production Research, which proposes a
// three-factor model specifically for autonomous robots in intralogistics:
//
//   1. Operational Continuity — stability of activity pattern over time
//   2. Productive Ratio       — time in productive motion vs powered-on time
//   3. Anomaly Frequency      — how often the robot deviates from its own norm
//
// All three factors are self-referential by design (Boysen et al., 2021,
// "Warehousing in the e-commerce era", EJOR): each robot is compared only to
// its own history, making scores valid across sites with different shift
// lengths and use cases.
//
//   RRI = (Consistency × 0.5) + (Productive Ratio × 0.3) + (Stability × 0.2)
//         × 100
//
//   Consistency (0.5, Operational Continuity):
//     recent 7d avg driving time / own 21d baseline avg (weekday-normalised).
//     Weekend handling: detected per robot from own uptime data, site-level
//     fallback if <4 weekend observations. Includes all days if unknown.
//
//   Productive Ratio (0.3, Productive Ratio):
//     recent moving_time_s / recent uptime_s.
//     Proxy for autonomous driving ratio (Fragapane et al., 2021, EJOR).
//     Not weekend-filtered — uptime is continuous regardless of schedule.
//
//   Stability (0.2, Anomaly Frequency):
//     1 - (anomalous_days / total_recent_days)
//     anomalous_day = daily km deviates >50% from robot's own 7d rolling avg.
//     Penalises erratic behaviour — a robot driving 14km one day and 0km the
//     next six scores lower than one driving 2km consistently every day, even
//     if weekly totals are the same.
//
// Weights rationale (Boysen-informed):
//   Consistency 0.5 — most predictive of real issues per Boysen (2021)
//   Productive ratio 0.3 — useful but context-dependent
//   Stability 0.2 — penalises erratic behaviour without dominating
//
// Future dimensions (not yet available in robot_stats.json):
//   Task completion rate, autonomous driving ratio, mean distance between
//   failures (Fragapane et al., 2021) — slot in as additional weighted
//   dimensions when data becomes available.
//
// New robots (<21d history): consistency estimated from site peer median
//   until own baseline is established. Flagged as "estimated".
// ─────────────────────────────────────────────────────────────────────────────

import { BASELINE_WINDOW_DAYS, RECENT_WINDOW_DAYS, INACTIVE_CUTOFF_DAYS } from "../constants.js";
import { addDays, detectOperatesWeekends, detectSiteOperatesWeekends, smartWeekdayFilter } from "../utils/date.js";

function daysBetween(isoA, isoB) {
    return Math.round(
        (new Date(isoB + "T00:00:00Z") - new Date(isoA + "T00:00:00Z")) / 86400000
    );
}

function clamp(v, min, max) {
    return Math.max(min, Math.min(max, v));
}

function avg(arr, fn) {
    if (arr.length === 0) return 0;
    return arr.reduce((s, r) => s + fn(r), 0) / arr.length;
}

/**
 * Compute Stability Score (Anomaly Frequency dimension from Bechtsis 2022).
 *
 * Builds a 7-day rolling average of daily km for each day in the robot's
 * history, then counts how many days in the recent window deviate >50% from
 * that rolling average. Returns 1 - (anomalous_days / total_recent_days).
 *
 * A robot driving consistently every day scores 1.0.
 * A robot with erratic spikes/drops scores lower.
 *
 * Requires at least 7 days of history before the recent window to build a
 * meaningful rolling average. Returns null if insufficient history.
 *
 * robotWknd / siteWknd: weekend operating pattern from detectOperatesWeekends.
 * Weekend days are stripped from both the rolling average build and the anomaly
 * count if the robot doesn't operate weekends — same logic as consistency score.
 */
function computeStabilityScore(allDays, recentDays, robotWknd, siteWknd) {
    if (allDays.length < 7 || recentDays.length === 0) return null;

    // Apply same smart weekend filter as consistency —
    // strip weekends from history if robot doesn't operate weekends
    const filteredAll    = smartWeekdayFilter(allDays,    robotWknd, siteWknd);
    const filteredRecent = smartWeekdayFilter(recentDays, robotWknd, siteWknd);

    if (filteredAll.length < 7 || filteredRecent.length === 0) return null;

    // Build daily km map from filtered history only
    const byDate = new Map();
    for (const r of filteredAll) {
        byDate.set(r.date, (byDate.get(r.date) || 0) + r.distance_m);
    }
    const sortedDates = Array.from(byDate.keys()).sort();
    if (sortedDates.length < 7) return null;

    // Build rolling 7d average per date (operating days only)
    const rollingAvg = new Map();
    for (let i = 6; i < sortedDates.length; i++) {
        const window    = sortedDates.slice(i - 6, i + 1);
        const windowAvg = window.reduce((s, d) => s + (byDate.get(d) || 0), 0) / window.length;
        rollingAvg.set(sortedDates[i], windowAvg);
    }

    // Count anomalous days in the filtered recent window
    let anomalousCount = 0;
    let countedDays    = 0;

    for (const r of filteredRecent) {
        const baseline = rollingAvg.get(r.date);
        if (baseline === undefined || baseline < 10) continue; // no rolling avg yet or near-zero baseline

        const actual = byDate.get(r.date) || 0;
        const ratio  = actual / baseline;
        countedDays++;
        if (ratio < 0.5 || ratio > 2.0) anomalousCount++;
    }

    if (countedDays < 3) return null; // not enough operating days to make a call

    return clamp(1 - anomalousCount / countedDays, 0, 1);
}

export function buildRobotHealth(rows, maxDate) {
    if (!maxDate || rows.length === 0) return new Map();

    const recentFrom    = addDays(maxDate, -(RECENT_WINDOW_DAYS - 1));
    const baselineFrom  = addDays(maxDate, -(BASELINE_WINDOW_DAYS + RECENT_WINDOW_DAYS - 1));
    const baselineTo    = addDays(maxDate, -RECENT_WINDOW_DAYS);
    const offlineCutoff = addDays(maxDate, -(INACTIVE_CUTOFF_DAYS - 1));

    // ── 1. Group rows by robot and by site ───────────────────────────────────
    const byRobot = new Map();
    const bySite  = new Map();

    for (const r of rows) {
        if (!byRobot.has(r.robotId)) {
            byRobot.set(r.robotId, {
                robotId:      r.robotId,
                robotName:    r.robotName,
                siteName:     r.siteName,
                client:       r.client,
                allDays:      [],
                recentDays:   [],
                baselineDays: [],
                firstDate:    r.date,
            });
        }
        const e = byRobot.get(r.robotId);
        e.allDays.push(r);
        if (r.date < e.firstDate) e.firstDate = r.date;
        if (r.date >= recentFrom)                           e.recentDays.push(r);
        if (r.date >= baselineFrom && r.date <= baselineTo) e.baselineDays.push(r);

        if (!bySite.has(r.siteName)) bySite.set(r.siteName, []);
        bySite.get(r.siteName).push(r);
    }

    // ── 2. Site-level weekend patterns ──────────────────────────────────────
    const siteWeekendPattern = new Map();
    for (const [siteName, siteRows] of bySite.entries()) {
        siteWeekendPattern.set(siteName, detectSiteOperatesWeekends(siteRows));
    }

    // ── 3. Site-level peer baseline for new robots ───────────────────────────
    const sitePeerBaseline = new Map();
    for (const [siteName, siteRows] of bySite.entries()) {
        const baselineRows = siteRows.filter(
            (r) => r.date >= baselineFrom && r.date <= baselineTo
        );
        if (baselineRows.length === 0) continue;

        const siteWknd = siteWeekendPattern.get(siteName);
        const robotAvgs = new Map();
        for (const r of baselineRows) {
            if (!robotAvgs.has(r.robotId)) robotAvgs.set(r.robotId, []);
            robotAvgs.get(r.robotId).push(r);
        }

        const perRobotAvg = Array.from(robotAvgs.entries()).map(([, days]) => {
            const robotWknd = detectOperatesWeekends(days);
            const filtered  = smartWeekdayFilter(days, robotWknd, siteWknd);
            return avg(filtered, (r) => r.moving_time_s);
        }).filter((v) => v > 0);

        if (perRobotAvg.length === 0) continue;

        perRobotAvg.sort((a, b) => a - b);
        const mid    = Math.floor(perRobotAvg.length / 2);
        const median = perRobotAvg.length % 2 === 0
            ? (perRobotAvg[mid - 1] + perRobotAvg[mid]) / 2
            : perRobotAvg[mid];

        sitePeerBaseline.set(siteName, median);
    }

    // ── 4. Score each robot ──────────────────────────────────────────────────
    const result = new Map();

    for (const e of byRobot.values()) {
        const lastActiveDate = e.allDays
            .filter((r) => r.distance_m > 0 || (r.uptime_s || 0) > 0)
            .reduce((best, r) => (!best || r.date > best ? r.date : best), null);

        // Offline / no data
        if (!lastActiveDate || lastActiveDate < offlineCutoff) {
            result.set(e.robotId, {
                rri: null, label: "Inactive", color: "#ff5f5f",
                estimated: false, isNew: false,
                consistencyScore: null, utilizationScore: null, stabilityScore: null,
                utilization: null, kmPerUptimeH: null, kmPerDrivingH: null,
                lastActiveDate, operatesWeekends: null,
                explanation: "No data received in the last 7 days. The robot may be powered off or disconnected.",
            });
            continue;
        }

        // ── Weekend pattern ──────────────────────────────────────────────────
        const robotWknd    = detectOperatesWeekends(e.allDays);
        const siteWknd     = siteWeekendPattern.get(e.siteName) ?? null;
        const resolvedWknd = robotWknd ?? siteWknd ?? true;

        // ── Factor 1: Consistency Score (Operational Continuity) ─────────────
        const daysOfHistory  = daysBetween(e.firstDate, maxDate);
        const isNew          = daysOfHistory < BASELINE_WINDOW_DAYS;
        let estimated        = false;
        let consistencyScore = null;

        const recentFiltered   = smartWeekdayFilter(e.recentDays,   robotWknd, siteWknd);
        const baselineFiltered = smartWeekdayFilter(e.baselineDays, robotWknd, siteWknd);
        const recentAvg        = avg(recentFiltered,   (r) => r.moving_time_s);
        const baselineAvg      = avg(baselineFiltered, (r) => r.moving_time_s);

        if (!isNew && baselineAvg > 300) {
            consistencyScore = clamp(recentAvg / baselineAvg, 0, 1);
        } else if (isNew) {
            const peerBaseline = sitePeerBaseline.get(e.siteName);
            if (peerBaseline && peerBaseline > 300 && recentAvg > 0) {
                consistencyScore = clamp(recentAvg / peerBaseline, 0, 1);
                estimated = true;
            }
        }

        // ── Factor 2: Productive Ratio (proxy for autonomous driving ratio) ──
        const recentUptime  = e.recentDays.reduce((s, r) => s + (r.uptime_s || 0), 0);
        const recentDriving = e.recentDays.reduce((s, r) => s + r.moving_time_s, 0);
        const utilizationScore = recentUptime > 0
            ? clamp(recentDriving / recentUptime, 0, 1)
            : null;

        // ── Factor 3: Stability Score (Anomaly Frequency) ───────────────────
        const stabilityScore = computeStabilityScore(e.allDays, e.recentDays, robotWknd, siteWknd);

        // ── RRI — three-factor weighted average ──────────────────────────────
        // Weights: Consistency 0.5, Productive Ratio 0.3, Stability 0.2
        // If a factor is unavailable, redistribute its weight to the others
        // proportionally so the score remains on a 0–100 scale.
        let rri = null;
        const factors = [
            { score: consistencyScore,  weight: 0.5 },
            { score: utilizationScore,  weight: 0.3 },
            { score: stabilityScore,    weight: 0.2 },
        ];

        const available = factors.filter((f) => f.score !== null);
        if (available.length > 0) {
            const totalWeight = available.reduce((s, f) => s + f.weight, 0);
            const weighted    = available.reduce((s, f) => s + f.score * f.weight, 0);
            rri = Math.round((weighted / totalWeight) * 100);

            // Cap at 70 if any factor is missing — can't reach Optimal without all three
            if (available.length < factors.length) {
                rri = Math.min(rri, 70);
            }
        }

        // ── Band label ───────────────────────────────────────────────────────
        let label, color;
        if (rri === null)    { label = "Insufficient data"; color = "#9caeb3"; }
        else if (rri >= 80)  { label = "Optimal";           color = "#7bff89"; }
        else if (rri >= 60)  { label = "Operational";       color = "#a8e063"; }
        else if (rri >= 40)  { label = "Underperforming";   color = "#f5a623"; }
        else if (rri >= 20)  { label = "At risk";           color = "#ff8c5f"; }
        else                 { label = "Inactive";          color = "#ff5f5f"; }

        // ── Supporting metrics ───────────────────────────────────────────────
        const totalDistance = e.allDays.reduce((s, r) => s + r.distance_m, 0);
        const totalDriving  = e.allDays.reduce((s, r) => s + r.moving_time_s, 0);
        const totalUptime   = e.allDays.reduce((s, r) => s + (r.uptime_s || 0), 0);
        const kmPerUptimeH  = totalUptime  > 0 ? (totalDistance / 1000) / (totalUptime  / 3600) : null;
        const kmPerDrivingH = totalDriving > 0 ? (totalDistance / 1000) / (totalDriving / 3600) : null;
        const utilization   = recentUptime > 0 ? recentDriving / recentUptime : null;

        const weekendNote = resolvedWknd === false
            ? " Weekends excluded from consistency (site does not operate weekends)."
            : robotWknd === null && siteWknd === null
                ? " Insufficient weekend data — all days included in consistency."
                : "";

        const stabilityNote = stabilityScore !== null
            ? ` Stability: ${Math.round(stabilityScore * 100)}% of recent days within normal range.`
            : " Stability score not yet available (insufficient history).";

        // ── Plain-English explanation ────────────────────────────────────────
        let explanation = "";
        const cPct = Math.round((consistencyScore || 0) * 100);
        const uPct = Math.round((utilizationScore || 0) * 100);

        if (estimated) {
            explanation = `New robot (${daysOfHistory} days of data). Consistency estimated from ${e.siteName} site peer median until 21 days of own history are available.${stabilityNote}${weekendNote}`;
        } else if (rri === null) {
            explanation = `Not enough data to compute a score. Needs at least 7 days of recent activity with uptime data.${weekendNote}`;
        } else if (rri >= 80) {
            explanation = `Driving consistently, spending a high proportion of uptime in motion, and behaving predictably day-to-day. Consistency: ${cPct}% of own baseline. Utilization: ${uPct}% of powered-on time.${stabilityNote}${weekendNote}`;
        } else if (rri >= 60) {
            explanation = `Performing within normal range across all three dimensions. Consistency: ${cPct}% of baseline. Utilization: ${uPct}% of uptime.${stabilityNote}${weekendNote}`;
        } else if (rri >= 40) {
            explanation = `Underperforming on one or more dimensions. Consistency: ${cPct}% of baseline. Utilization: ${uPct}% of uptime.${stabilityNote}${weekendNote}`;
        } else if (rri >= 20) {
            explanation = `At risk — significant underperformance on one or more dimensions. Consistency: ${cPct}% of baseline. Utilization: ${uPct}% of uptime.${stabilityNote}${weekendNote}`;
        } else {
            explanation = `Very little or no driving activity in the past 7 days. May be powered off, idle, or experiencing an issue.${stabilityNote}${weekendNote}`;
        }

        result.set(e.robotId, {
            rri, label, color, estimated, isNew,
            consistencyScore, utilizationScore, stabilityScore,
            utilization, kmPerUptimeH, kmPerDrivingH,
            lastActiveDate, operatesWeekends: resolvedWknd,
            explanation,
        });
    }

    return result;
}